import MarkerClusterGroup from "@changey/react-leaflet-markercluster";
import "leaflet/dist/leaflet.css";
import "@changey/react-leaflet-markercluster/dist/styles.min.css";
import { useContext, useEffect, useState } from "react";
import { MapContainer, useMap, useMapEvents } from "react-leaflet";
import { GeoJSON } from "react-leaflet/GeoJSON";
import { GipContext, ThemeContext } from "state";
import QueryContext from "state/query";
import states from "../../assets/data/map_states.json";
import Marker from "./marker";
import L from "leaflet";
import { useCountries, useGTM } from "hooks";
import { faList } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const isMarkerInsidePolygon = (markerLatLng, poly) => {
  //If not a polygon
  if (poly._latlngs === undefined) return false;
  var polyPoints = poly._latlngs[0];
  var x = markerLatLng[0],
    y = markerLatLng[1];

  var inside = false;
  for (var i = 0, j = polyPoints.length - 1; i < polyPoints.length; j = i++) {
    var xi = polyPoints[i].lat,
      yi = polyPoints[i].lng;
    var xj = polyPoints[j].lat,
      yj = polyPoints[j].lng;

    var intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
};

function Map() {
  const { dataLayer } = useGTM();
  const map = useMap();
  const mapEvents = useMapEvents({
    zoomend: (e) => {
      dataLayer.push({ event: "cefic_gip_map.map.zoom", value: map.getZoom() });
    },
  });
  const {
    gips,
    activeProject,
    hoverProject,
    defaultMapSettings,
    setShowProjectList,
    setMap,
  } = useContext(GipContext);
  const { disclaimer } = useContext(ThemeContext);
  const { countries, technologies, statuses } = useContext(QueryContext);
  const { countries: allCountries } = useCountries();
  const [markers, setMarkers] = useState([]);

  useEffect(() => {
    let markers = [];
    if (gips?.length) {
      gips?.forEach((project) => {
        project?.acf?.locations?.forEach((l) => {
          if (l.geolocation?.markers?.length) {
            const m = l.geolocation.markers[0];
            //Filter marker in countries only
            if (!countries.length || countries.includes(l.country)) {
              markers.push({
                position: [m.lat, m.lng],
                projectId: project.id,
                projectName: project.title.rendered, //needed for gtm push
                countryId: l.country,
                uuid: m.uuid,
              });
            }
          }
        });
      });
    }
    setMarkers(markers);
  }, [gips, countries]);
  useEffect(() => {
    setMap(map);
  }, [map, setMap]);
  useEffect(() => {
    //If no filter, set all layer opacity at 1
    if (!countries.length && !technologies.length && !statuses.length) {
      map.eachLayer((layer) => {
        if (layer._latlngs !== undefined) {
          layer.setStyle({
            fillOpacity: 1,
          });
        }
      });
    } else {
      //Highlight countries with markers
      map.eachLayer((layer) => {
        //If not poligon
        if (layer._latlngs === undefined) {
          return;
        }
        let layerFound = null;
        markers.forEach((m) => {
          if (isMarkerInsidePolygon(m.position, layer)) {
            layerFound = layer;
            return;
          }
        });
        //If no marker on polygon
        if (layerFound === null) {
          layer.setStyle({
            fillOpacity: 0.5,
          });
        } else {
          layerFound.setStyle({
            fillOpacity: 1,
          });
        }
      });
    }
  }, [map, markers, countries.length, technologies.length, statuses.length]);

  const createClusterCustomIcon = function(cluster) {
    return L.divIcon({
      html: `<span>${cluster.getChildCount()}</span>`,
      className:
        "bg-white !w-8 !h-8 rounded-full flex justify-center items-center drop-shadow-md text-primary font-lato font-bold text-center pt-[8px] opacity-85",
      iconSize: L.point(40, 40, true),
    });
  };

  return (
    <>
      <GeoJSON
        key={"cefic_eu_map"}
        data={states}
        style={(feature) => ({
          fillColor: "#79B777",
          color: "#D6ECF5",
          weight: 1,
        })}
      />
      {/* Create one MarkerClusterGourp per country */}
      {allCountries?.map((c) => (
        <MarkerClusterGroup
          key={c.id}
          chunkedLoading
          iconCreateFunction={createClusterCustomIcon}
          spiderLegPolylineOptions={{
            color: "#FFF",
            opacity: 0.85,
            weight: 2,
          }}
          showCoverageOnHover={false}
          maxClusterRadius={(currentZoom) => {
            //If minZoom, group by country (large radius)
            if (currentZoom === defaultMapSettings.minZoom) {
              return 1500;
            }
            return 50;
          }}
          eventHandlers={{
            //Remove change opacity in leaflet.markercluster/src/MarkerCluster.Spiderfier.js
            spiderfied: (e) => {
              e.cluster.setOpacity(0.85);
            },
            unspiderfied: (e) => {
              e.cluster.setOpacity(0.85);
            },
          }}
        >
          {/* Only include markers that belongs to country */}
          {markers
            ?.filter((m) => m.countryId === c.id)
            ?.map((m) => (
              <Marker
                position={m.position}
                projectId={m.projectId}
                countryId={m.countryId}
                key={m.uuid}
                active={
                  (activeProject?.projectId === m.projectId &&
                    activeProject?.countryId === m.countryId) ||
                  (hoverProject?.projectId === m.projectId &&
                    hoverProject?.countryId === m.countryId)
                }
              ></Marker>
            ))}
        </MarkerClusterGroup>
      ))}
      <div className="absolute bottom-8 text-center w-full z-600">
        <button
          onClick={() => setShowProjectList(true)}
          className="flex gap-4 flex-row items-center m-auto bg-gray-50 shadow-xl rounded-lg z-600 px-20 py-3 text-primary-500 text-center text-sm sm:hidden"
        >
          <FontAwesomeIcon icon={faList} />
          <span className="uppercase tracking-widest font-lato text-xs">
            Project list
          </span>
        </button>
      </div>
      {disclaimer?.url && (
        <a href={disclaimer.url} target="_blank">
          <button
            className="bg-white z-500 text-xs bg-opacity-30 rounded text-gray-500 absolute bottom-2 left-2 p-[2px] hover:bg-opacity-80"
            dangerouslySetInnerHTML={{ __html: disclaimer.title }}
          />
        </a>
      )}
    </>
  );
}

const MapWrapper = () => {
  const { activeProject, defaultMapSettings, showProjectList } =
    useContext(GipContext);
  return (
    <>
      <div
        className={`${activeProject || showProjectList ? "block" : "hidden"
          } sm:hidden fixed bg-black opacity-30 top-0 w-full h-full z-500`}
      ></div>

      <MapContainer
        center={defaultMapSettings.center}
        zoom={defaultMapSettings.zoom}
        minZoom={defaultMapSettings.minZoom}
        maxZoom={defaultMapSettings.maxZoom}
        scrollWheelZoom={true}
        style={{ width: "100%", height: "100%", background: "#D6ECF5" }}
      >
        <Map />
      </MapContainer>
    </>
  );
};

export default MapWrapper;
